<template>
  <RevInfoBlock
    v-if="flashSalesWarning"
    :content="flashSalesWarning.content"
    :cta="flashSalesWarning.cta"
    :icon="IconWarning"
    :title="flashSalesWarning.title"
    variant="warning"
    @click="flashSalesWarning.onClick"
  />
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'

import type { CartItem } from '@backmarket/nuxt-layer-checkout/api/cart/types'
import { getTrackingProduct } from '@backmarket/nuxt-layer-checkout/composables/useCheckoutTracking'
import { getFlashSalesErrorType } from '@backmarket/nuxt-layer-checkout/utils/flash-sales'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { IconWarning } from '@ds/icons/IconWarning'

import useHandleUnauthorizedUser from '~/scopes/checkout/composables/useHandleUnauthorizedUser'

import translations from './FlashSalesWarning.translations'

const props = defineProps<{
  items: Array<CartItem>
}>()

const emit = defineEmits<{
  updating: []
  updated: []
}>()

const experiments = useExperiments()
const i18n = useI18n()
const $httpFetch = useHttpFetch()

const errorType = computed(() => getFlashSalesErrorType(props.items))

const nonFlashSalesItems = computed(() =>
  props.items.filter((item) => !item.promoCodes?.length),
)

const isFlashSalesEnabled = experiments['experiment.flashSales'] === 'enabled'
const { trackRemoveFromCart, trackFlashSalesErrorImpression } = useTracking()
const { handleUnauthorizedUser } = useHandleUnauthorizedUser()
const {
  market: { defaultCurrency },
} = useMarketplace()

const deleteItem = async (item: CartItem) => {
  try {
    await $httpFetch('/bm/cart/update_quantity', {
      method: 'POST',
      body: {
        action: 'delete',
        listing_id: item.listingId,
        new_quantity: item.quantity,
        listing_price: item.price,
      },
    })

    const product = getTrackingProduct(item, defaultCurrency)
    if (product) {
      trackRemoveFromCart(product)
    }
  } catch (error) {
    await handleUnauthorizedUser(
      error as Record<string, unknown>,
      '[CHECKOUT] Unhandled error updating quantity',
    )
  }
}

const cleanNonFlashSalesItems = async () => {
  emit('updating')

  await nonFlashSalesItems.value.reduce(
    (promise, item) => promise.then(() => deleteItem(item)),
    Promise.resolve(),
  )

  emit('updated')
}

const warningTitles = {
  TITLE_OR_BRAND_MATCH_FAILED: i18n(
    translations.flashSalesWarningIneligibleTitle,
  ),
  DEFAULT: i18n(translations.flashSalesWarningNotAppliedTitle),
}

const warningCtas = {
  TITLE_OR_BRAND_MATCH_FAILED: i18n(
    translations.flashSalesWarningIneligibilityCta,
  ),
}

const warningMessages = computed(() => ({
  PROMOTION_INACTIVE: i18n(translations.flashSalesWarningInactive),
  PROMOTION_ENDED: i18n(translations.flashSalesWarningEnded),
  PROMOTION_NOT_STARTED: i18n(translations.flashSalesWarningNotStarted),
  TOTAL_REDEMPTION_LIMIT_EXCEEDED: i18n(translations.flashSalesWarningSoldOut),
  USER_REDEMPTION_LIMIT_EXCEEDED: i18n(
    translations.flashSalesWarningIneligible,
  ),
  TOTAL_LISTING_QUANTITY_EXCEEDS_MAX: i18n(
    translations.flashSalesWarningQuantityMax,
  ),
  TOTAL_LISTING_QUANTITY_BELOW_MIN: i18n(
    translations.flashSalesWarningQuantityMin,
  ),
  TITLE_OR_BRAND_MATCH_FAILED: `${i18n(translations.flashSalesWarningIncompatible, { product_title: nonFlashSalesItems.value.map((i) => i.title).join(', ') })} `,
}))

const flashSalesWarning = computed(() => {
  if (!isFlashSalesEnabled || !errorType.value) return null

  const title =
    warningTitles[errorType.value as keyof typeof warningTitles] ??
    warningTitles.DEFAULT
  const content =
    warningMessages.value[
      errorType.value as keyof typeof warningMessages.value
    ] ?? null
  const cta = warningCtas[errorType.value as keyof typeof warningCtas]

  return {
    title,
    content,
    ...(cta
      ? {
          cta,
          onClick: cleanNonFlashSalesItems,
        }
      : {}),
  }
})

watch(
  () => errorType.value,
  (value) => {
    if (isFlashSalesEnabled && value) {
      trackFlashSalesErrorImpression({
        block_name: 'flash_sales_error_message',
        block_value: value,
        block_zone: 'cart',
      })
    }
  },
  {
    immediate: true,
  },
)
</script>
